import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw } from 'lucide-react';
import { damagesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import dayjs from '../../utils/dayjs';
import { printDamageDispatch } from '../../utils/pdf';

const Field = ({ label, children }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-medium text-gray-900">{children}</div>
  </div>
);

const DamageDispatchDetail = () => {
  const { id } = useParams();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    damagesAPI.getDispatch(id)
      .then((res) => setDispatch(res.data))
      .catch(() => addNotification({ type: 'error', title: 'Damages', message: 'Shipment not found' }))
      .finally(() => setLoading(false));
  }, [id, addNotification]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await printDamageDispatch(dispatch, formatPrice);
    } catch {
      addNotification({ type: 'error', title: 'PDF', message: 'Failed to create the PDF' });
    } finally {
      setDownloading(false);
    }
  };

  if (!dispatch) {
    return <div className="p-8 text-center text-sm text-gray-500">{loading ? 'Loading...' : 'Shipment not found.'}</div>;
  }

  const firstRecorded = dispatch.items[0]?.CreatedDate;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link to="/damages/dispatches" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            Damages shipment <span className="font-mono">{dispatch.DispatchNo}</span>
          </h1>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          {downloading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download PDF
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 grid grid-cols-2 gap-4 md:grid-cols-5 text-sm">
        <Field label="Sent">{dayjs(dispatch.DispatchDate).format('DD MMM YYYY, h:mm A')}</Field>
        <Field label="Damages from">
          {dispatch.PeriodStart
            ? dayjs(dispatch.PeriodStart).format('DD MMM YYYY')
            : firstRecorded ? dayjs(firstRecorded).format('DD MMM YYYY') : '-'}
        </Field>
        <Field label="Pieces">{dispatch.TotalQuantity}</Field>
        <Field label="Value at POS price">{formatPrice(dispatch.TotalValue)}</Field>
        <Field label="Sent by">{dispatch.CreatedByName || '-'}</Field>
        {dispatch.Remarks && (
          <div className="col-span-2 md:col-span-5">
            <div className="text-xs text-gray-500">Remarks</div>
            <div className="text-gray-900 whitespace-pre-line">{dispatch.Remarks}</div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="px-4 py-2 text-left font-medium">#</th>
              <th className="px-4 py-2 text-left font-medium">Item</th>
              <th className="px-4 py-2 text-right font-medium">Qty</th>
              <th className="px-4 py-2 text-left font-medium">Reason</th>
              <th className="px-4 py-2 text-left font-medium">Recorded</th>
              <th className="px-4 py-2 text-right font-medium">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {dispatch.items.map((d, i) => (
              <tr key={d.ID} className="align-top">
                <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                <td className="px-4 py-2">
                  <Link to={`/items/${d.Item_ID}/history`} className="font-medium text-gray-900 hover:text-blue-700">
                    {d.ItemName || `Item #${d.Item_ID}`}
                  </Link>
                  <div className="text-xs text-gray-500">Barcode {d.Barcode || '-'}</div>
                </td>
                <td className="px-4 py-2 text-right font-semibold">{d.Quantity}</td>
                <td className="px-4 py-2">
                  <div>{d.Reason}</div>
                  {d.Remarks && <div className="text-xs text-gray-500 break-words">{d.Remarks}</div>}
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">
                  <div>{dayjs(d.CreatedDate).format('DD MMM YYYY, h:mm A')}</div>
                  <div>{d.CreatedByName || '-'}</div>
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{formatPrice(d.LineValue)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold">
            <tr>
              <td className="px-4 py-2" />
              <td className="px-4 py-2">Total</td>
              <td className="px-4 py-2 text-right">{dispatch.TotalQuantity}</td>
              <td className="px-4 py-2" colSpan={2} />
              <td className="px-4 py-2 text-right whitespace-nowrap">{formatPrice(dispatch.TotalValue)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default DamageDispatchDetail;
